import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Button, Divider, Grid, Card, CardContent, Container, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Tooltip, Snackbar, Alert, CircularProgress, useTheme, InputAdornment, useMediaQuery, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
    Add as AddIcon, WorkOff, Close as CloseIcon, Search as SearchIcon, ArrowUpward,
    ArrowDownward, UnfoldMore, DomainDisabled, DomainAdd, Check, Visibility as VisibilityIcon
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';
import { getRole, getToken } from '../../../services/auth';
import { jwtDecode } from 'jwt-decode';

const itemsPerPage = 6;

const renderBusinessTypes = (user) => {
    const selectedActivities = user?.typeBusiness
        ? user.typeBusiness
            .map((tb) => tb?.name)
            .filter(Boolean)
        : [];

    return selectedActivities.length === 0
        ? 'Nenhum tipo selecionado'
        : selectedActivities.length === 1
            ? selectedActivities[0]
            : `${selectedActivities.slice(0, -1).join(', ')} e ${selectedActivities[selectedActivities.length - 1]}`;
};

const EntrepreneurPage = () => {
    const location = useLocation();
    const [typeUser, setTypeUser] = useState(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [page, setPage] = useState(1);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openActivateDialog, setOpenActivateDialog] = useState(false);
    const [userToDeleteId, setUserToDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });
    const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
    const [selectedBusinessType, setSelectedBusinessType] = useState('');

    const fetchUsers = async () => {
        try {
            setLoading(true);
            let response;
            if (getRole() === 'team') {
                const decoded = jwtDecode(getToken());
                const id = decoded.id;
                response = await api.get(`/economic-agents/creator/${id}`);
            } else {
                response = await api.get('/economic-agents');
            }
            setUsers(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar os empreendedores', 'error');
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        setTypeUser(location.pathname.split('/')[1]);
    }, [location.pathname]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const neighborhoods = useMemo(() => {
        const unique = [...new Set(users.map(user => user?.neighborhood?.name).filter(Boolean))];
        return unique.sort();
    }, [users]);

    const normalizeStringForSearch = (str) => {
        if (typeof str !== 'string') return '';
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/\s+/g, '');
    };

    const businessTypes = useMemo(() => {
        const allTypes = users.flatMap(user =>
            user?.typeBusiness?.map(tb => tb?.name).filter(Boolean) || []
        );
        const unique = [...new Set(allTypes)];

        return unique.sort((a, b) => {
            const normalizedA = normalizeStringForSearch(a);
            const normalizedB = normalizeStringForSearch(b);
            if (normalizedA < normalizedB) return -1;
            if (normalizedA > normalizedB) return 1;
            return 0;
        });
    }, [users]);

    const filteredUsers = useMemo(() => {
        let filtered = users;

        const normalizedSearchTerm = normalizeStringForSearch(searchTerm);

        filtered = filtered.filter(user => {
            const normalizedUserName = normalizeStringForSearch(user?.name);
            const normalizedNeighborhoodName = normalizeStringForSearch(user?.neighborhood?.name);

            return (
                normalizedUserName.includes(normalizedSearchTerm) ||
                normalizedNeighborhoodName.includes(normalizedSearchTerm)
            );
        });

        if (selectedNeighborhood) {
            filtered = filtered.filter(user =>
                user?.neighborhood?.name === selectedNeighborhood
            );
        }

        if (selectedBusinessType) {
            filtered = filtered.filter(user =>
                user?.typeBusiness?.some(tb => tb?.name === selectedBusinessType)
            );
        }

        return filtered;
    }, [users, searchTerm, selectedNeighborhood, selectedBusinessType]);

    const visibleUsers = useMemo(() => {
        let sortedUsers = [...filteredUsers];
        if (sortConfig.key) {
            sortedUsers.sort((a, b) => {
                const aValue = a[sortConfig.key] || '';
                const bValue = b[sortConfig.key] || '';
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedUsers.slice(startIndex, endIndex);
    }, [filteredUsers, page, sortConfig]);

    const handleDelete = (id) => {
        setUserToDeleteId(id);
        setOpenConfirmDialog(true);
    };

    const handleActive = (id) => {
        setUserToDeleteId(id);
        setOpenActivateDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.put(`/economic-agents/desativate/${userToDeleteId}`);
            showSnackbar('Empreendedor desativado com sucesso.', 'success');
            fetchUsers();
        } catch (error) {
            showSnackbar('Erro ao desabilitar empreendedor', 'error');
            console.error('Error deleting user:', error);
        } finally {
            setOpenConfirmDialog(false);
            setUserToDeleteId(null);
        }
    };

    const handleConfirmActivate = async () => {
        try {
            await api.put(`/economic-agents/activate/${userToDeleteId}`);
            showSnackbar('Empreendedor ativado com sucesso.', 'success');
            fetchUsers();
        } catch (error) {
            showSnackbar('Erro ao ativar empreendedor', 'error');
            console.error('Error deleting user:', error);
        } finally {
            setOpenActivateDialog(false);
            setUserToDeleteId(null);
        }
    };

    const handleCloseActiveDialog = () => {
        setOpenActivateDialog(false);
        setUserToDeleteId(null);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setUserToDeleteId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const maskPhoneNumber = (phone) => {
        if (!phone || phone.trim() === '') return 'Não informado';
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{2})(\d{1})(\d{4})(\d{4})$/);
        if (match) return `(${match[1]}) ${match[2]}${match[3]}-${match[4]}`;
        return phone;
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={isMobile ? 'xs' : 'xl'} sx={{ mt: isMobile ? 1 : 0, mb: 4, px: isMobile ? 1 : 2 }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ justifyContent: 'center', mt: 0 }}>
                    <Grid item xs={12} sm={10} md={8}>
                        <Card sx={{
                            borderRadius: isMobile ? '8px' : '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            p: isMobile ? 1 : 2,
                            mt: 0
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: isMobile ? 0 : 0
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'center' : 'center',
                                    mb: isMobile ? 2 : 1,
                                    width: '100%',
                                    gap: isMobile ? 2 : 0
                                }}>
                                    <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#10641c', fontWeight: '600' }}>
                                            Empreendedores
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#666', mt: 0.5 }}>
                                            Total: {<strong>{filteredUsers.length}</strong>}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => navigate(`/${typeUser}/entrepreneur-registration`)}
                                        sx={{
                                            bgcolor: '#10641c',
                                            '&:hover': { bgcolor: '#0a3d0f' },
                                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                                            px: isMobile ? 2 : 3
                                        }}
                                    >
                                        Novo Empreendedor
                                    </Button>
                                </Box>
                                <Divider sx={{ mb: isMobile ? 2 : 2, width: '100%' }} />
                                <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Buscar empreendedor..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        sx={{
                                            height: '45px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '6px' : '8px',
                                                fontSize: isMobile ? '1rem' : '1rem',
                                                height: 'inherit',
                                                padding: '0 14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#ced4da'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10641c'
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c'
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: isMobile ? '1rem' : '1rem'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize={isMobile ? 'medium' : 'medium'} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        width: '100%',
                                        mb: isMobile ? 2 : 2,
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        gap: isMobile ? 2 : 2,
                                    }}
                                >
                                    <FormControl sx={{ flex: 1, width: isMobile ? '100%' : '200px' }}>
                                        <InputLabel
                                            id="neighborhood-filter-label"
                                            sx={{
                                                '&.Mui-focused': {
                                                    color: 'green',
                                                },
                                                transform: 'translate(14px, 12px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                },
                                            }}
                                        >
                                            Bairro/Distrito
                                        </InputLabel>
                                        <Select
                                            labelId="neighborhood-filter-label"
                                            value={selectedNeighborhood}
                                            label="Bairro/Distrito"
                                            onChange={(e) => {
                                                setSelectedNeighborhood(e.target.value);
                                                setPage(1);
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 200,
                                                    },
                                                },
                                            }}
                                            sx={{
                                                borderRadius: '8px',
                                                height: '45px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderRadius: '8px',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'green',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'green',
                                                },
                                            }}
                                        >
                                            <MenuItem value="" dense>Todos</MenuItem>
                                            {neighborhoods.map((neighborhood) => (
                                                <MenuItem key={neighborhood} value={neighborhood} dense>
                                                    {neighborhood}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ flex: 1, width: isMobile ? '100%' : '200px' }}>
                                        <InputLabel
                                            id="business-type-filter-label"
                                            sx={{
                                                '&.Mui-focused': {
                                                    color: 'green',
                                                },
                                                transform: 'translate(14px, 12px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                },
                                            }}
                                        >
                                            Tipo de Empreendimento
                                        </InputLabel>
                                        <Select
                                            labelId="business-type-filter-label"
                                            value={selectedBusinessType}
                                            label="Tipo de Empreendimento"
                                            onChange={(e) => {
                                                setSelectedBusinessType(e.target.value);
                                                setPage(1);
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 205,
                                                        width: '100%',
                                                        maxWidth: isMobile ? '75vw' : '37vw',
                                                    },
                                                },
                                            }}
                                            sx={{
                                                borderRadius: '8px',
                                                height: '45px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderRadius: '8px',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'green',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'green',
                                                },
                                            }}
                                        >
                                            <MenuItem value="" dense sx={{ maxWidth: '100%', whiteSpace: 'normal' }}>Todos</MenuItem>
                                            {businessTypes.map((type) => (
                                                <MenuItem key={type} value={type} dense sx={{ maxWidth: '100%', whiteSpace: 'normal' }}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                                        <CircularProgress size={isMobile ? 30 : 40} />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        {isMobile ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {visibleUsers.length === 0 ? (
                                                    <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center' }}>
                                                        Nenhum empreendedor encontrado.
                                                    </Typography>
                                                ) : (
                                                    visibleUsers.map((user) => (
                                                        <Card key={user.id} sx={{
                                                            borderRadius: '8px',
                                                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                                                            p: 2
                                                        }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                                    {user.name}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                                    <strong>Código:</strong> {user.code}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                                    <strong>Telefone:</strong> {maskPhoneNumber(user.phone)}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                                    <strong>Bairro/Distrito:</strong> {user.neighborhood.name}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                                    <strong>Tipo:</strong> {renderBusinessTypes(user)}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                                    <strong>Situação:</strong>
                                                                    <span style={{ color: user.active === 1 ? '#10641c' : '#d32f2f' }}>
                                                                        {user.active === 1 ? ' Ativo' : ' Inativo'}
                                                                    </span>
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                                                    <Tooltip title="Ver Detalhes">
                                                                        <IconButton
                                                                            onClick={() => navigate(`/${typeUser}/DetailsEntrepreneur/${user.id}`)}
                                                                            sx={{ color: '#10641c', p: 1 }}
                                                                        >
                                                                            <VisibilityIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    {typeUser !== 'team' && (
                                                                        user.active === 1 ? (
                                                                            <Tooltip title="Inativar">
                                                                                <IconButton
                                                                                    onClick={() => handleDelete(user.id)}
                                                                                    sx={{ color: '#d32f2f', p: 1 }}
                                                                                >
                                                                                    <DomainDisabled fontSize="medium" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip title="Ativar">
                                                                                <IconButton
                                                                                    onClick={() => handleActive(user.id)}
                                                                                    sx={{ color: '#0a3d0f', p: 1 }}
                                                                                >
                                                                                    <DomainAdd fontSize="medium" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    ))
                                                )}
                                            </Box>
                                        ) : (
                                            // Desktop: Table layout
                                            <TableContainer sx={{ maxWidth: '100%' }}>
                                                <Table sx={{
                                                    minWidth: 700,
                                                    width: '100%',
                                                    tableLayout: 'fixed',
                                                    '& .MuiTableCell-head': { fontWeight: 'bold' },
                                                    '& .MuiTableCell-root': {
                                                        fontSize: '0.875rem',
                                                        p: 1
                                                    }
                                                }} aria-label="users table">
                                                    <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                                                        <TableRow>
                                                            <TableCell align="center" sx={{ width: '20%', cursor: 'pointer' }} onClick={() => requestSort('code')}>
                                                                <Box display="flex" alignItems="center" justifyContent="center">
                                                                    Código
                                                                    {sortConfig.key === 'code' ? (
                                                                        sortConfig.direction === 'ascending' ?
                                                                            <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                            <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                    ) : (
                                                                        <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ width: '25%', cursor: 'pointer' }} onClick={() => requestSort('name')}>
                                                                <Box display="flex" alignItems="center" justifyContent="center">
                                                                    Nome do Empreendimento
                                                                    {sortConfig.key === 'name' ? (
                                                                        sortConfig.direction === 'ascending' ?
                                                                            <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                            <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                    ) : (
                                                                        <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ width: '20%' }}>Telefone</TableCell>
                                                            <TableCell align="center" sx={{ width: '15%' }}>Bairro/Distrito</TableCell>
                                                            <TableCell align="center" sx={{ width: '20%' }}>Tipo de Empreendimento</TableCell>
                                                            <TableCell align="center" sx={{ width: '20%' }}>Situação</TableCell>
                                                            <TableCell align="center" sx={{ width: '20%' }}>Ações</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {visibleUsers.length === 0 ? (
                                                            <TableRow>
                                                                <TableCell align="center" colSpan={7}>
                                                                    <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                                                                        Nenhum empreendedor encontrado.
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (
                                                            visibleUsers.map((user) => (
                                                                <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                                                                    <TableCell align="center">{user.code}</TableCell>
                                                                    <TableCell align="center">{user.name}</TableCell>
                                                                    <TableCell align="center">{maskPhoneNumber(user.phone)}</TableCell>
                                                                    <TableCell align="center">{user.neighborhood.name}</TableCell>
                                                                    <TableCell align="center">
                                                                        {renderBusinessTypes(user)}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <span style={{ color: user.active === 1 ? '#10641c' : '#d32f2f' }}>
                                                                            {user.active === 1 ? 'Ativo' : 'Inativo'}
                                                                        </span>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Tooltip title="Ver Detalhes">
                                                                            <IconButton
                                                                                onClick={() => navigate(`/${typeUser}/DetailsEntrepreneur/${user.id}`)}
                                                                                sx={{ color: '#10641c', p: 1 }}
                                                                            >
                                                                                <VisibilityIcon fontSize="medium" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {typeUser !== 'team' && (
                                                                            user.active === 1 ? (
                                                                                <Tooltip title="Inativar">
                                                                                    <IconButton
                                                                                        onClick={() => handleDelete(user.id)}
                                                                                        sx={{ color: '#d32f2f', p: 1 }}
                                                                                    >
                                                                                        <DomainDisabled fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            ) : (
                                                                                <Tooltip title="Ativar">
                                                                                    <IconButton
                                                                                        onClick={() => handleActive(user.id)}
                                                                                        sx={{ color: '#0a3d0f', p: 1 }}
                                                                                    >
                                                                                        <DomainAdd fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {filteredUsers.length >= 8 && (
                    <Box sx={{ mt: isMobile ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                        <Paginate
                            count={Math.ceil(filteredUsers.length / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            size={isMobile ? 'small' : 'medium'}
                        />
                    </Box>
                )}

                {/* Diálogo de confirmação de desativação */}
                <Dialog
                    open={openConfirmDialog}
                    onClose={handleCloseConfirmDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'sm'}
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', textAlign: "center" }}>
                        Confirmar Desativação
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja desativar este empreendedor?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseConfirmDialog}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<WorkOff />}
                        >
                            Inativar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de confirmação de ativação */}
                <Dialog
                    open={openActivateDialog}
                    onClose={handleCloseActiveDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'sm'}
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ bgcolor: '#10641c', color: 'white', textAlign: "center" }}>
                        Confirmar Ativação
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja ativar o cadastro deste empreendedor?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseActiveDialog}
                            color="error"
                            variant="contained"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmActivate}
                            color="error"
                            variant="contained"
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<Check />}
                        >
                            Ativar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar para feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%', fontSize: isMobile ? '0.9rem' : '0.875rem' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default EntrepreneurPage;